import { Injectable } from '@angular/core';
import { UnitTypesInBridge } from '../models';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { ToothEditorSharingService } from '@modules/tooth-editor/services/tooth-editor-sharing.service';
import { DefaultCrownStore } from '@modules/tooth-editor/state/default-crown-store';
import { CrownDefaultProperties } from '@modules/tooth-editor/models/default-crown-data';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { IntuitiveBridge } from '../models/intuitive-bridge';
import { ShadeSystem } from '@shared/models/shade-system';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { PartialToothUpdate } from '@modules/teeth-diagram/state/teeth-diagram-store';

@Injectable({ providedIn: 'root' })
export class RxIntuitiveCopyInBridgeService {
	constructor(
		private readonly sharingService: ToothEditorSharingService,
		private readonly defaultCrownStore: DefaultCrownStore,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService,
		private readonly shellQuery: ShellQuery
	) {}

	/**
	 * Records crown-form values as per-bridge defaults (first-value-wins).
	 * Each property is written only once per bridge; subsequent calls with different values are ignored.
	 * @param crownForm source of the material, specification, and shade data
	 * @param selectedBridge bridge whose defaults are being populated
	 */
	updateSharedDefaults(crownForm: CrownForm, selectedBridge: IntuitiveBridge) {
		const bridgeIndex = selectedBridge.index;

		if (bridgeIndex && bridgeIndex > 0) {
			this.defaultCrownStore.setDefaultData(crownForm.material as number, CrownDefaultProperties.MaterialId, bridgeIndex);
			this.defaultCrownStore.setDefaultData(crownForm.specification as number, CrownDefaultProperties.SpecificationId, bridgeIndex);

			const primaryToothId = crownForm.teethIds[0];

			if (crownForm.shadeBody != null && crownForm.shadeSystem != null && primaryToothId != null) {
				this.defaultCrownStore.setDefaultBodyData(crownForm.shadeBody, bridgeIndex, crownForm.shadeSystem, primaryToothId);
			}
		}
	}

	/**
	 * Removes all stored defaults for the given bridge so they do not bleed into a new bridge.
	 * @param selectedBridge bridge whose defaults should be cleared
	 */
	clearSharedDefaults(selectedBridge: IntuitiveBridge) {
		this.defaultCrownStore.clearDefaultData(selectedBridge.index);
	}

	/**
	 * Computes per-tooth sharing updates for the V1 alwaysCopy + whenDefaultsProvided rules
	 * after an indication is assigned inside a bridge. Returns one entry per matched tooth;
	 * the caller is responsible for writing the results to the store.
	 */
	computeBridgeIndicationSharingUpdates(
		selectedToothIds: number[],
		unitTypeInBridge: UnitTypesInBridge,
		bridgeTeeth: Tooth[]
	): Array<{ toothId: number; resetTooth: Partial<Tooth> }> {
		const convertedUnitType = unitTypeInBridge === UnitTypesInBridge.ImplantBased ? UnitTypes.ImplantBased : null;
		const updates: Array<{ toothId: number; resetTooth: Partial<Tooth> }> = [];

		for (const toothId of selectedToothIds) {
			const tooth = bridgeTeeth.find(t => t.ToothID === toothId);

			if (!tooth) {
				continue;
			}

			const shared = this.sharingService.getToothWithSharedProps(bridgeTeeth, {
				...tooth,
				ToothInBridgeTypeID: unitTypeInBridge,
				ShadeSystemId: this.getShadeSystemOptionsAndSelection(tooth.ShadeSystemId).shadeSystem.Id
			});

			const resetTooth = this.sharingService.resetToothIfNeeded(shared.tooth, convertedUnitType);

			updates.push({ toothId, resetTooth });
		}

		return updates;
	}

	shareDefaulPropsToBridgeTeeth(targetTeeth: Tooth[], sourceTooth: Tooth): { toothId: number; partialToUpdate: PartialToothUpdate }[] {
		const sharedTeeth = this.sharingService.shareManyPropsToTeeth(targetTeeth, sourceTooth);

		return sharedTeeth
			.filter(({ tooth, sharedProps }) => tooth.ToothID != null && sharedProps.length > 0)
			.map(({ tooth, sharedProps }) => {
				const partialToUpdate: PartialToothUpdate = {
					...sharedProps.reduce((acc, property) => ({ ...acc, [property]: tooth[property] }), {})
				};

				return { toothId: tooth.ToothID ?? 0, partialToUpdate };
			});
	}

	private getShadeSystemOptionsAndSelection(shadeSystemIdFromTooth?: number | null): {
		shadeSystem: ShadeSystem;
		options: ShadeSystem[];
	} {
		const defaultShadeSystemOptions = this.rxShadeSystemRulesService.getDefaultShadeSystemOptions();
		const userSettingsShadeSystemOption = this.getDefaultShadeSystemFromUserSettings(defaultShadeSystemOptions);
		const shadeSystemSelection = defaultShadeSystemOptions.find(option => option.Id === shadeSystemIdFromTooth);

		return {
			shadeSystem: shadeSystemSelection || userSettingsShadeSystemOption || defaultShadeSystemOptions[0],
			options: defaultShadeSystemOptions
		};
	}
	private getDefaultShadeSystemFromUserSettings(defaultShadeSystemOptions: ShadeSystem[]): ShadeSystem | undefined {
		const shadeSystemId = this.shellQuery.userSettings?.ShadeSystemId;

		return defaultShadeSystemOptions?.find(option => option.Id === shadeSystemId);
	}
}
